import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { formatCurrency, formatDate, formatDateTime, getDatePreset } from '../utils/formatters';
import DateRangeFilter from '../components/DateRangeFilter';
import ExportButton from '../components/ExportButton';
import ThreadPanel from '../components/ThreadPanel';

export default function Orders() {
  const { orderId: selectedOrderId } = useParams();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [dateRange, setDateRange] = useState(getDatePreset('today'));
  const [filters, setFilters] = useState({ status: '', search: '' });
  const [loading, setLoading] = useState(true);
  const [statuses, setStatuses] = useState([]);

  useEffect(() => {
    api.get('/api/orders/statuses', { params: dateRange }).then(({ data }) => setStatuses(data.statuses || [])).catch(() => {});
  }, [dateRange]);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/orders', {
        params: { ...dateRange, ...filters, page, limit: 50 }
      });
      setOrders(data.orders);
      setTotal(data.total);
    } catch (err) {
      console.error('Orders fetch error:', err);
    }
    setLoading(false);
  }, [dateRange, filters, page]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  useEffect(() => {
    if (selectedOrderId) {
      api.get(`/api/orders/${selectedOrderId}`).then(({ data }) => {
        setSelectedOrder(data.order);
        setLineItems(data.lineItems || []);
      }).catch(() => {});
    }
  }, [selectedOrderId]);

  function openOrder(order) {
    setSelectedOrder(order);
    setLineItems([]);
    navigate(`/orders/${order.id}`);
    api.get(`/api/orders/${order.id}`).then(({ data }) => {
      setSelectedOrder(data.order);
      setLineItems(data.lineItems || []);
    }).catch(() => {});
  }

  function closeDetail() {
    setSelectedOrder(null);
    setLineItems([]);
    navigate('/orders');
  }

  const formatTime = (dateStr) => {
    if (!dateStr) return '--';
    const d = new Date(dateStr);
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' });
  };

  const statusBadge = (status) => {
    const map = {
      COMPLETED: 'badge-success', DELIVERED: 'badge-success',
      SHIPPED: 'badge-info', IN_TRANSIT: 'badge-info', AWAITING_COLLECTION: 'badge-info',
      PROCESSING: 'badge-warning', AWAITING_SHIPMENT: 'badge-warning', ON_HOLD: 'badge-warning', UNPAID: 'badge-warning',
      CANCELLED: 'badge-danger', REFUNDED: 'badge-danger',
    };
    return <span className={`badge ${map[status] || 'badge-default'}`}>{status?.replace(/_/g, ' ') || 'UNKNOWN'}</span>;
  };

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Orders</h2>
        <ExportButton endpoint="/api/orders/export" params={dateRange} filename="orders-export.csv" />
      </div>

      <DateRangeFilter onChange={setDateRange} initial="today" />

      <div className="filter-bar">
        <select value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })} aria-label="Filter by order status">
          <option value="">All Statuses</option>
          {statuses.map(s => <option key={s.status} value={s.status}>{s.status.replace(/_/g, ' ')} ({s.count})</option>)}
        </select>
        <input placeholder="Search order ID, buyer, or name..." value={filters.search}
               onChange={e => setFilters({ ...filters, search: e.target.value })} style={{ maxWidth: '250px' }} />
      </div>

      {loading && <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}><div className="spinner" /></div>}

      {!loading && <div className="card">
        <div className="table-container sticky-col">
          <table>
            <thead>
              <tr>
                <th scope="col">Order ID</th><th scope="col">Date</th><th scope="col">Time</th><th scope="col">Customer</th><th scope="col">Status</th><th scope="col">GMV</th><th scope="col">Net Sales</th><th scope="col">Shipping</th>
              </tr>
            </thead>
            <tbody>
              {orders.map(o => (
                <tr key={o.id} className="clickable-row" onClick={() => openOrder(o)} tabIndex={0}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openOrder(o); } }}
                  role="button" aria-label={`Order ${o.order_id}`}>
                  <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{o.order_id}</td>
                  <td>{formatDate(o.created_time)}</td>
                  <td style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{formatTime(o.created_time)}</td>
                  <td>
                    {o.customer_name ? (
                      <div>
                        <div style={{ fontWeight: 500 }}>{o.customer_name}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>{o.buyer_id}</div>
                      </div>
                    ) : (
                      <span>{o.buyer_id}</span>
                    )}
                  </td>
                  <td>{statusBadge(o.order_status)}</td>
                  <td>{formatCurrency(o.gmv)}</td>
                  <td>{formatCurrency(o.net_sales)}</td>
                  <td>{formatCurrency(o.shipping_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="pagination">
          <span className="pagination-info">Showing {orders.length} of {total} orders</span>
          <div className="pagination-btns">
            <button className="btn btn-secondary btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</button>
            <button className="btn btn-secondary btn-sm" disabled={orders.length < 50} onClick={() => setPage(p => p + 1)}>Next</button>
          </div>
        </div>
      </div>}

      {selectedOrder && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-label={`Order ${selectedOrder.order_id} details`}
          onClick={closeDetail} onKeyDown={e => { if (e.key === 'Escape') closeDetail(); }}>
          <div className="detail-panel" onClick={e => e.stopPropagation()} style={{ maxWidth: '640px' }}>
            <div className="detail-panel-header">
              <div>
                <h3 style={{ margin: 0 }}>Order {selectedOrder.order_id}</h3>
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                  {formatDate(selectedOrder.created_time)} at {formatTime(selectedOrder.created_time)}
                  {selectedOrder.paid_time && <span> &middot; Paid {formatTime(selectedOrder.paid_time)}</span>}
                </div>
              </div>
              <button className="detail-panel-close" onClick={closeDetail} aria-label="Close order details">&times;</button>
            </div>

            {/* Status & Customer */}
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid var(--border)' }}>
              <div>{statusBadge(selectedOrder.order_status)}</div>
              <div style={{ fontSize: '13px' }}>
                {selectedOrder.customer_name && (
                  <span style={{ fontWeight: 600, color: 'var(--text)', marginRight: '8px' }}>{selectedOrder.customer_name}</span>
                )}
                <span style={{ fontFamily: 'monospace', fontSize: '12px', color: 'var(--text-light)' }}>{selectedOrder.buyer_id}</span>
              </div>
            </div>

            {/* Line Items */}
            {lineItems.length > 0 && (() => {
              // Group duplicate items by product+sku
              const grouped = [];
              const keyMap = {};
              for (const li of lineItems) {
                const key = `${li.product_id}|${li.sku_id}`;
                if (keyMap[key] !== undefined) {
                  grouped[keyMap[key]].quantity += li.quantity;
                  grouped[keyMap[key]].totalPrice += li.sale_price * li.quantity;
                } else {
                  keyMap[key] = grouped.length;
                  grouped.push({ ...li, totalPrice: li.sale_price * li.quantity });
                }
              }
              const totalQty = grouped.reduce((s, li) => s + li.quantity, 0);
              return (
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '10px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Items ({totalQty})</div>
                <div style={{ border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden' }}>
                  {grouped.map((li, i) => (
                    <div key={li.id} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '10px 14px', background: i % 2 === 0 ? 'var(--bg)' : 'white',
                      borderBottom: i < grouped.length - 1 ? '1px solid var(--border)' : 'none'
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '13px', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {li.product_name || 'Unknown Product'}
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--text-light)', marginTop: '2px' }}>
                          {li.sku_name && <span>{li.sku_name}</span>}
                          {li.seller_sku && <span> &middot; SKU: {li.seller_sku}</span>}
                          {li.product_cogs != null && <span> &middot; COGS: {formatCurrency(li.product_cogs)}</span>}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexShrink: 0, marginLeft: '12px' }}>
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)', minWidth: '30px', textAlign: 'center' }}>×{li.quantity}</span>
                        <span style={{ fontSize: '13px', fontWeight: 600, minWidth: '65px', textAlign: 'right' }}>{formatCurrency(li.totalPrice)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              );
            })()}

            {/* Financials */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '10px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Financials</div>
              <div className="auto-stack" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div style={{ padding: '10px 14px', background: 'var(--bg)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: 600 }}>GMV</div>
                  <div style={{ fontSize: '16px', fontWeight: 700 }}>{formatCurrency(selectedOrder.gmv)}</div>
                </div>
                <div style={{ padding: '10px 14px', background: 'var(--bg)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: 600 }}>Net Sales</div>
                  <div style={{ fontSize: '16px', fontWeight: 700 }}>{formatCurrency(selectedOrder.net_sales)}</div>
                </div>
                <div style={{ padding: '10px 14px', background: 'var(--bg)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: 600 }}>Shipping</div>
                  <div style={{ fontSize: '14px', fontWeight: 600 }}>{formatCurrency(selectedOrder.shipping_amount)}</div>
                </div>
                <div style={{ padding: '10px 14px', background: 'var(--bg)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: 600 }}>Seller Shipping Cost</div>
                  <div style={{ fontSize: '14px', fontWeight: 600 }}>{formatCurrency(selectedOrder.seller_shipping_cost || 0)}</div>
                </div>
                <div style={{ padding: '10px 14px', background: 'var(--bg)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: 600 }}>Seller Discount</div>
                  <div style={{ fontSize: '14px', fontWeight: 600 }}>{formatCurrency(selectedOrder.seller_discount_amount)}</div>
                </div>
                <div style={{ padding: '10px 14px', background: 'var(--bg)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: 600 }}>Platform Discount</div>
                  <div style={{ fontSize: '14px', fontWeight: 600 }}>{formatCurrency(selectedOrder.platform_discount_amount)}</div>
                </div>
                <div style={{ padding: '10px 14px', background: 'var(--bg)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: 600 }}>Tax</div>
                  <div style={{ fontSize: '14px', fontWeight: 600 }}>{formatCurrency(selectedOrder.order_tax)}</div>
                </div>
                <div style={{ padding: '10px 14px', background: 'var(--bg)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: 600 }}>Commission</div>
                  <div style={{ fontSize: '14px', fontWeight: 600 }}>{formatCurrency(selectedOrder.commission)}</div>
                </div>
              </div>
            </div>

            {/* Fee Breakdown */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '10px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Fees
                {!selectedOrder.fees_fetched_at && <span style={{ fontSize: '11px', fontWeight: 400, color: 'var(--text-light)', textTransform: 'none', letterSpacing: 0, marginLeft: '8px' }}>Estimated — settles in ~8 days</span>}
              </div>
              <div className="auto-stack" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                {selectedOrder.fees_fetched_at ? (
                  <>
                    <div style={{ padding: '10px 14px', background: 'var(--bg)', borderRadius: '8px' }}>
                      <div style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: 600 }}>Referral Fee</div>
                      <div style={{ fontSize: '14px', fontWeight: 600 }}>{formatCurrency(selectedOrder.referral_fee || 0)}</div>
                    </div>
                    <div style={{ padding: '10px 14px', background: 'var(--bg)', borderRadius: '8px' }}>
                      <div style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: 600 }}>Affiliate Commission</div>
                      <div style={{ fontSize: '14px', fontWeight: 600 }}>{formatCurrency((selectedOrder.affiliate_commission || 0) + (selectedOrder.affiliate_ads_commission || 0))}</div>
                    </div>
                    <div style={{ padding: '10px 14px', background: 'var(--bg)', borderRadius: '8px' }}>
                      <div style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: 600 }}>Total Fees</div>
                      <div style={{ fontSize: '14px', fontWeight: 600 }}>{formatCurrency(selectedOrder.total_fees || 0)}</div>
                    </div>
                    <div style={{ padding: '10px 14px', background: 'var(--bg)', borderRadius: '8px' }}>
                      <div style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: 600 }}>Settlement Amount</div>
                      <div style={{ fontSize: '14px', fontWeight: 600 }}>{formatCurrency(selectedOrder.settlement_amount || 0)}</div>
                    </div>
                    <div style={{ padding: '10px 14px', background: 'var(--bg)', borderRadius: '8px' }}>
                      <div style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: 600 }}>Actual Shipping</div>
                      <div style={{ fontSize: '14px', fontWeight: 600 }}>{formatCurrency(selectedOrder.actual_shipping_fee || 0)}</div>
                    </div>
                    <div style={{ padding: '10px 14px', background: 'var(--bg)', borderRadius: '8px' }}>
                      <div style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: 600 }}>Customer Paid Shipping</div>
                      <div style={{ fontSize: '14px', fontWeight: 600 }}>{formatCurrency(selectedOrder.customer_paid_shipping_fee || 0)}</div>
                    </div>
                  </>
                ) : (
                  <div style={{ padding: '10px 14px', background: 'var(--bg)', borderRadius: '8px' }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: 600 }}>Estimated Fees</div>
                    <div style={{ fontSize: '14px', fontWeight: 600 }}>{formatCurrency(selectedOrder.estimated_fees || 0)}</div>
                  </div>
                )}
              </div>
            </div>

            <div style={{ fontSize: '11px', color: 'var(--text-light)', marginBottom: '16px' }}>Last updated: {selectedOrder.last_updated}</div>

            <ThreadPanel entityType="order" entityId={selectedOrder.id} />
          </div>
        </div>
      )}
    </div>
  );
}
